import { ReactElement } from 'react'
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td, TdId, IdWrapper } from 'components/v2/Table'
import { CopyToClipboard } from '@10xLabs/react-web/components/CopyToClipboard'

import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canWrite } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { StyledLink } from 'components/v2/Link'
import { GTBusinessClient } from 'gql/types.generated'
import { Link, NavLink } from 'react-router-dom'
import { BusinessClientListActions } from 'features/BusinessClients/components/BusinessClientList/Actions'
import { safeFormatMessage } from 'utils/intl'
import { verticalMessages } from 'features/Companies/components/Vertical/messages'

interface IClientListProps {
  clients: GTBusinessClient[]
}

enum ClientStatusEnum {
  active = 'active',
  inactive = 'inactive'
}

const Status = styled.div<{ status: string }>`
  text-transform: capitalize;
  font-weight: 500;
  white-space: nowrap;
  border-radius: 24px;
  color: #fff;
  width: 96px;
  height: 24px;
  text-align: center;
  font-size: 14px;
  line-height: 24px;

  ${({ status }): string => {
    let backgroundColor

    switch (status) {
      case ClientStatusEnum.active:
        backgroundColor = '#00C875'
        break
      case ClientStatusEnum.inactive:
        backgroundColor = '#A86EEB'
        break
    }

    return `background-color: ${backgroundColor};`
  }}
`

const ClientStatus = ({ status }: { status: ClientStatusEnum }): ReactElement => {
  const intl = useIntl()

  if (statusMessages[status]) {
    return <Status status={status}>{intl.formatMessage(statusMessages[status])}</Status>
  }

  return <div>{status}</div>
}

const statusMessages = {
  [ClientStatusEnum.active]: {
    id: 'companyClients.status.active',
    defaultMessage: 'Active'
  },
  [ClientStatusEnum.inactive]: {
    id: 'companyClients.status.inactive',
    defaultMessage: 'Inactive'
  }
}

const BusinessClientListView = ({ clients }: IClientListProps): ReactElement => {
  const login = useLogin()
  const intl = useIntl()
  const { policies } = login.accessToken
  const canSeeActions = canWrite(FEATURES.BUSINESS_CLIENT, policies)

  return (
    <div>
      <TableContainer overflow>
        <Table>
          <Thead>
            <Tr>
              {canSeeActions && <Th noUppercase></Th>}
              <Th>
                <FormattedMessage id="companyClients.id" defaultMessage="CLIENT ID" />
              </Th>
              <Th>
                <FormattedMessage id="companyClients.status" defaultMessage="Status" />
              </Th>
              <Th>
                <FormattedMessage id="companyClients.name" defaultMessage="CLIENT NAME" />
              </Th>
              <Th>
                <FormattedMessage id="companyClients.kam" defaultMessage="KAM" />
              </Th>
              <Th>
                <FormattedMessage id="companyClients.vertical" defaultMessage="Vertical" />
              </Th>
              <Th>
                <FormattedMessage id="companyClients.sites" defaultMessage="Sites" />
              </Th>
              <Th>
                <FormattedMessage id="companyClients.customer" defaultMessage="Customers" />
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {clients.map((client) => {
              const verticals = client.companies.map(({ vertical }) => vertical)
              return (
                <Tr key={client.id}>
                  {canSeeActions && (
                    <Td>
                      <BusinessClientListActions businessClient={client} />
                    </Td>
                  )}
                  <TdId>
                    <IdWrapper>
                      <StyledLink as={Link} to={`/business-clients/${client.id}/users`}>
                        {client.id.substring(0, 8).toUpperCase()}
                      </StyledLink>
                      <CopyToClipboard str={client.id} />
                    </IdWrapper>
                  </TdId>
                  <Td>
                    <ClientStatus status={client.isActive ? ClientStatusEnum.active : ClientStatusEnum.inactive} />
                  </Td>
                  <Td>{client.name}</Td>
                  <Td>{client.kamUser ? `${client.kamUser.firstName} ${client.kamUser.lastName}` : '-'}</Td>
                  <Td>
                    {verticals
                      .map((vertical) => safeFormatMessage({ intl, messages: verticalMessages, key: vertical }))
                      .join(', ')}
                  </Td>
                  <Td>
                    <StyledLink as={NavLink} to={`/business-sites?clientId=${client.id}`}>
                      <span>{client.numberOfSites}</span>
                    </StyledLink>
                  </Td>
                  <Td>
                    <StyledLink as={NavLink} to={`/business-customers?clientId=${client.id}`}>
                      <span>{client.numberOfCustomers}</span>
                    </StyledLink>
                  </Td>
                </Tr>
              )
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </div>
  )
}

export default BusinessClientListView
